import React, { useState, useEffect, useRef } from 'react';
import { Modal, Form, Button, Row, Col, Spinner } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import dayjs from 'dayjs';
import { useAuthContext } from 'context/authContext';
import {
  useCreateReminder,
  useOrganisationUsers,
  useUpdateReminder
} from 'hooks/useReminders';
import PropTypes from 'prop-types';
import 'react-datepicker/dist/react-datepicker.css';

const REMINDER_TYPES = [
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'payment', label: 'Payment' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'task', label: 'Task' },
  { value: 'update', label: 'Update' }
];

const REPEAT_OPTIONS = [
  { value: 'none', label: 'Does not repeat' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' }
];

// One place to build the form state from a reminder (edit) or defaults (new)
const buildFormData = (source = {}, user) => ({
  title: source.title || '',
  description: source.description || '',
  reminder_type: source.reminder_type || 'task',
  due_date: source.due_date ? new Date(source.due_date) : new Date(),
  due_time: source.due_time ? String(source.due_time).slice(0, 5) : '',
  repeat_rule: source.repeat_rule || 'none',
  entity_type: source.entity_type || null,
  entity_id: source.entity_id || null,
  entity_label: source.entity_label || source.entity_name || '',
  // Edit: the one assignee on the row. New: any number of staff, each of
  // whom gets their own copy of the reminder (the table holds one assignee)
  assigned_to_user_id: source.assigned_to_user_id || user?.id || '',
  assigned_to_user_ids: source.assigned_to_user_id
    ? [source.assigned_to_user_id]
    : user?.id
    ? [user.id]
    : []
});

const CreateReminderModal = ({
  show,
  onHide,
  defaultValues = {},
  editingReminder = null
}) => {
  const { user } = useAuthContext();
  const { users, isLoading: usersLoading } = useOrganisationUsers();
  const { createReminderAsync, isCreating } = useCreateReminder();
  const { updateReminder, isUpdating } = useUpdateReminder();
  const prevShowRef = useRef(false);

  const isEditMode = !!editingReminder;

  const [formData, setFormData] = useState(() =>
    buildFormData(editingReminder || defaultValues, user)
  );

  // Only reset form when modal OPENS (show changes from false to true)
  useEffect(() => {
    if (show && !prevShowRef.current) {
      // Modal just opened
      setFormData(buildFormData(editingReminder || defaultValues, user));
    }
    prevShowRef.current = show;
  }, [show]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData(buildFormData({}, user));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!formData.title.trim()) return;

    if (isEditMode) {
      // Update existing reminder
      updateReminder(
        {
          id: editingReminder.id,
          data: {
            title: formData.title.trim(),
            description: formData.description.trim() || null,
            reminder_type: formData.reminder_type,
            due_date: dayjs(formData.due_date).format('YYYY-MM-DD'),
            due_time: formData.due_time || null,
            repeat_rule: formData.repeat_rule || 'none',
            entity_type: formData.entity_type || null,
            entity_id: formData.entity_id || null,
            assigned_to_user_id: formData.assigned_to_user_id || user?.id
          }
        },
        {
          onSuccess: () => {
            resetForm();
            onHide();
          }
        }
      );
    } else {
      // One reminder per chosen person (falls back to the creator)
      const assignees = formData.assigned_to_user_ids.length
        ? formData.assigned_to_user_ids
        : [user?.id];
      const base = {
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        reminder_type: formData.reminder_type,
        due_date: dayjs(formData.due_date).format('YYYY-MM-DD'),
        due_time: formData.due_time || null,
        repeat_rule: formData.repeat_rule || 'none',
        entity_type: formData.entity_type || null,
        entity_id: formData.entity_id || null,
        created_by_user_id: user?.id
      };
      try {
        await Promise.all(
          assignees.map(assignedId =>
            createReminderAsync({ ...base, assigned_to_user_id: assignedId })
          )
        );
        resetForm();
        onHide();
      } catch (error) {
        console.error('Error creating reminders:', error);
      }
    }
  };

  const userOptions = users.map(u => ({
    value: u.id,
    label: `${u.first_name} ${u.last_name}${u.id === user?.id ? ' (Me)' : ''}`
  }));
  const selectedAssignees = userOptions.filter(o =>
    formData.assigned_to_user_ids.includes(o.value)
  );
  const allSelected =
    users.length > 0 && selectedAssignees.length === users.length;

  const handleClose = () => {
    resetForm();
    onHide();
  };

  const isSubmitting = isCreating || isUpdating;

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {isEditMode ? 'Edit Reminder' : 'Create Reminder'}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="fs-0 fw-medium">Title</Form.Label>
            <Form.Control
              type="text"
              placeholder="Reminder title..."
              value={formData.title}
              onChange={e => handleChange('title', e.target.value)}
              required
              style={{ fontSize: '1rem', padding: '0.75rem' }}
            />
          </Form.Group>

          <Row className="g-3 mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fs-0 fw-medium">Type</Form.Label>
                <Form.Select
                  value={formData.reminder_type}
                  onChange={e => handleChange('reminder_type', e.target.value)}
                  style={{ fontSize: '1rem', padding: '0.75rem' }}
                >
                  {REMINDER_TYPES.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fs-0 fw-medium">Due Date</Form.Label>
                <DatePicker
                  selected={formData.due_date}
                  onChange={date => handleChange('due_date', date)}
                  dateFormat="dd/MM/yyyy"
                  minDate={new Date()}
                  className="form-control w-100 py-2"
                  wrapperClassName="w-100"
                  placeholderText="Due date"
                  style={{ fontSize: '1rem', padding: '0.75rem' }}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="g-3 mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fs-0 fw-medium">
                  Time (optional)
                </Form.Label>
                <Form.Control
                  type="time"
                  value={formData.due_time}
                  onChange={e => handleChange('due_time', e.target.value)}
                  style={{ fontSize: '1rem', padding: '0.75rem' }}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fs-0 fw-medium">Repeat</Form.Label>
                <Form.Select
                  value={formData.repeat_rule}
                  onChange={e => handleChange('repeat_rule', e.target.value)}
                  style={{ fontSize: '1rem', padding: '0.75rem' }}
                >
                  {REPEAT_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Form.Select>
                {formData.repeat_rule !== 'none' && (
                  <Form.Text className="text-600">
                    Completing it schedules the next one automatically.
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
          </Row>

          {formData.entity_id && (
            <div className="mb-3 fs--1">
              <span className="text-600">Linked to </span>
              <span className="fw-semi-bold">
                {formData.entity_type === 'donor' ? 'donor' : 'beneficiary'}{' '}
                {formData.entity_label || ''}
              </span>
            </div>
          )}

          <Form.Group className="mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <Form.Label className="fs-0 fw-medium mb-0">Assign To</Form.Label>
              {!isEditMode && users.length > 1 && (
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 fs--1"
                  onClick={() =>
                    handleChange(
                      'assigned_to_user_ids',
                      allSelected ? [] : users.map(u => u.id)
                    )
                  }
                >
                  {allSelected
                    ? 'Clear'
                    : `Everyone in my branch (${users.length})`}
                </Button>
              )}
            </div>
            {isEditMode ? (
              <Form.Select
                value={formData.assigned_to_user_id}
                onChange={e =>
                  handleChange('assigned_to_user_id', e.target.value)
                }
                disabled={usersLoading}
                className="mt-1"
                style={{ fontSize: '1rem', padding: '0.75rem' }}
              >
                <option value="">Select user...</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.first_name} {u.last_name}
                    {u.id === user?.id ? ' (Me)' : ''}
                  </option>
                ))}
              </Form.Select>
            ) : (
              <>
                <Select
                  isMulti
                  inputId="reminder-assignees"
                  className="mt-1"
                  classNamePrefix="react-select"
                  placeholder="Choose one or more people..."
                  options={userOptions}
                  value={selectedAssignees}
                  isLoading={usersLoading}
                  isDisabled={usersLoading}
                  closeMenuOnSelect={false}
                  onChange={opts =>
                    handleChange(
                      'assigned_to_user_ids',
                      (opts || []).map(o => o.value)
                    )
                  }
                  components={{ IndicatorSeparator: () => null }}
                />
                {selectedAssignees.length > 1 && (
                  <Form.Text className="text-600">
                    Each of the {selectedAssignees.length} people gets their
                    own reminder to complete.
                  </Form.Text>
                )}
              </>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fs-0 fw-medium">
              Description (optional)
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              placeholder="Add a description..."
              value={formData.description}
              onChange={e => handleChange('description', e.target.value)}
              style={{ fontSize: '1rem', padding: '0.75rem' }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} size="lg">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={
              isSubmitting ||
              !formData.title.trim() ||
              (!isEditMode && formData.assigned_to_user_ids.length === 0)
            }
            size="lg"
          >
            {isSubmitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {isEditMode ? 'Updating...' : 'Creating...'}
              </>
            ) : isEditMode ? (
              'Update Reminder'
            ) : (
              'Create Reminder'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

CreateReminderModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  defaultValues: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    reminder_type: PropTypes.string,
    due_date: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date)
    ]),
    due_time: PropTypes.string,
    repeat_rule: PropTypes.string,
    entity_type: PropTypes.string,
    entity_id: PropTypes.string,
    entity_label: PropTypes.string,
    assigned_to_user_id: PropTypes.string
  }),
  editingReminder: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    reminder_type: PropTypes.string,
    due_date: PropTypes.string,
    assigned_to_user_id: PropTypes.string
  })
};

export default CreateReminderModal;
